import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, Inject, Injector, NgModuleRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgSelectComponent } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';
import { RestProviderService } from 'src/app/services/rest-provider/rest-provider.service';
import { DynamicCompilationService } from 'src/app/services/dynamic-compilation/dynamic-compilation.service';
import { RouteNavigationService } from 'src/app/services/dynamic-routes-loader/route-navigation.service';
import { DynamicFieldsManagerService } from 'src/app/services/dynamic-fields/dynamic-fields-manager.service';
import { FileUploadStandaloneService } from 'src/app/services/file-upload-standalone/file-upload-standalone.service';
import { TranslationsProviderService } from 'src/app/services/translations-provider/translations-provider.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { ResponseStatus, SemDynamicComponent } from 'src/app/shared/components/sem-dynamic/sem-dynamic.component';

// Data passed by SemSelect (quick-create mode "full-form").
export interface QuickCreateFormDialogData {
  formLink: string;          // SerializationObjectFormLink "{dynamicFormId}-{ViewName}"
  formUrl: string;           // route.Url; GET returns the backend-rendered form HTML
  title: string;             // route.Title for the header
  initialModel?: any;        // e.g. { Title: typedText }
}

export interface QuickCreateFormDialogResult {
  saved: boolean;
  model?: any;               // full saved model (response Model of POST api/data)
}

/**
 * Hosts a backend dynamic form inside a MatDialog ("full-form" quick-create).
 *
 * The form HTML is the same string the route page uses (it already contains the
 * `<form #f="ngForm" (ngSubmit)="onSubmit(f)">` wrapper emitted by ModelFormControl), so no
 * master page is needed. It is compiled into a throwaway subclass of SemDynamicComponent the
 * same way SemDynamicControlComponent.initDynamicForm() compiles task forms
 * (sem-dynamic-control.component.ts). The subclass runs in "dialog hosted" mode
 * (SemDynamicComponent.isDialogHosted): no route-driven model load, no navigation after save,
 * no file upload; on a successful save it calls hostedSaveHandler with the saved model and
 * this dialog closes with { saved: true, model }.
 */
@Component({
    selector: 'sem-quick-create-form-dialog',
    templateUrl: './sem-quick-create-form-dialog.component.html',
    styleUrls: ['./sem-quick-create-form-dialog.component.css'],
    standalone: false
})
export class SemQuickCreateFormDialogComponent implements OnInit, OnDestroy {

  @ViewChild('vc', { read: ViewContainerRef, static: true }) vc: ViewContainerRef;
  cmpRef: ComponentRef<any> | null = null;
  loading: boolean = true;
  error: string | null = null;
  // utility.reqResponse is global page state (cleared 6 s after a page save error); only show it
  // once this dialog has actually attempted a save.
  private submitAttempted: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<SemQuickCreateFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: QuickCreateFormDialogData,
    private restProvider: RestProviderService,
    private dynamicCompilation: DynamicCompilationService,
    private routeNav: RouteNavigationService,
    private injector: Injector,
    private ngModuleRef: NgModuleRef<any>,
    public translationsService: TranslationsProviderService,
    public utility: UtilityService,
    private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.dialogRef.updateSize('720px', 'auto');
    this.dialogRef.disableClose = true;
    this.loadForm();
  }

  ngOnDestroy(): void {
    if (this.cmpRef) {
      this.cmpRef.destroy();
      this.cmpRef = null;
    }
  }

  // translate(key) returns the key itself when the dictionary has no entry
  // (translations-provider.service.ts); fall back to the Slovenian default in that case.
  tr(key: string, fallback: string): string {
    const v = this.translationsService.translate(key);
    return v === key ? fallback : v;
  }

  get saving(): boolean {
    return !!this.cmpRef?.instance?.loading;
  }

  get serverError(): string | null {
    if (!this.submitAttempted || !this.utility.reqResponse) return null;
    const st = this.utility.reqStatus;
    return st === ResponseStatus.Error || st === ResponseStatus.ErrorMandatoryFields
      ? this.utility.reqMessage
      : null;
  }

  save(): void {
    if (!this.cmpRef || this.cmpRef.instance.loading) return;
    this.submitAttempted = true;
    // full pipeline: validation, popup-before-save, POST api/data, hostedSaveHandler
    this.cmpRef.instance.formSubmit();
  }

  cancel(): void {
    // header X and Cancel button: never close while POST api/data is in flight
    if (this.saving) return;
    this.dialogRef.close({ saved: false } as QuickCreateFormDialogResult);
  }

  private async loadForm(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      // already-registered routes keep their raw form HTML (route-navigation.service.ts)
      const cached = this.routeNav.loadedRoutes.find(r => r && r.Url === this.data.formUrl);
      let html: string = cached ? cached.Html : await firstValueFrom(this.restProvider.getDataGET(this.data.formUrl)) as any;
      html = DynamicCompilationService.sanitizeTemplate(html);

      if (!html || html.length === 0) {
        throw new Error('empty form template for ' + this.data.formUrl);
      }

      // Pattern copied from sem-dynamic-control.component.ts (initDynamicForm):
      // throwaway SemDynamicComponent subclass with its own DynamicFieldsManagerService.
      // FileUploadStandaloneService is providedIn: null, so it must be listed here exactly as the
      // route page does (route-navigation.service.ts registerRoute), or the constructor fails with NG0201.
      const tmpCmp = Component({
        standalone: false,
        template: html,
        providers: [DynamicFieldsManagerService, FileUploadStandaloneService],
        changeDetection: ChangeDetectionStrategy.Default
      })(class extends SemDynamicComponent { });

      // Prototype assignment works only for fields declared WITHOUT an initializer
      // (SerializationObjectFormLink, isRoutePage). isDialogHosted has `= false`, which the
      // constructor writes on every instance, so it is set on the instance below instead.
      const proto = tmpCmp.prototype as any;
      proto.SerializationObjectFormLink = this.data.formLink;
      proto.isRoutePage = false;

      const factories = await this.dynamicCompilation.compileComponentModule(tmpCmp, { providers: [NgSelectComponent] });
      const f = factories.componentFactories[factories.componentFactories.length - 1];

      this.cmpRef = f.create(this.injector, [], null, this.ngModuleRef);
      // ngOnInit runs on the first detectChanges below, so instance fields set here are visible to it.
      this.cmpRef.instance.isDialogHosted = true;
      this.cmpRef.instance.name = 'QuickCreateFormDialogComponent';
      this.cmpRef.instance.hostedInitialModel = this.data.initialModel || null;
      this.cmpRef.instance.hostedSaveHandler = (model: any) => {
        this.dialogRef.close({ saved: true, model } as QuickCreateFormDialogResult);
      };

      this.vc.clear();
      this.vc.insert(this.cmpRef.hostView);
      this.cmpRef.changeDetectorRef.detectChanges();
    } catch (e) {
      console.error('[QuickCreateFormDialog] failed to load form', this.data, e);
      const detail = (e && (e.message || e.statusText || String(e))) || '';
      this.error = this.tr('WebGalis_QuickCreate_LoadError', 'Obrazca ni bilo mogoče naložiti.') + (detail ? ' (' + detail + ')' : '');
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }
}
